/* eslint-disable react/no-array-index-key */
import HandsupInfo from '@/components/organisms/HandsupInfo';
import useGetConstructionAgreementInfo from '@/hooks/api/useGetContractAgreementInfo';
import useSetLoading from '@/hooks/useSetLoading';
import alertState from '@/states/alertState';
import {
  ConstructionAgreementInfoState,
  ConstructionAgreementInfoStateType,
} from '@/states/constructionAgreementInfoState';
import { OptionsInfoState } from '@/states/optionsInfoState';
import { ConstructionAgreementInfoResponse } from '@/types/api/mypage/constructionAgreementInfo';
import api from '@/utils/api';
import { getSigV4HeadersForGet, isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl, Constants } from '@/utils/constants';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import Heading from '../atoms/Heading';
import LinkButton from '../atoms/LinkButton';

type Props = {
  anchor: string;
};

/**
 * 工事費残債と工事承諾導線をまとめて表示し、Hands-up 契約時は追加残債情報も差し込むセクション。
 * 工事まわりの確認事項が複数 API にまたがるため、この画面でまとめて取得して一箇所に集約する。
 */
const ConstructionInfo = ({ anchor }: Props) => {
  const initContructionInfo = {
    name: '',
    construction_remaining_count: '',
    construction_remaining_price: '',
  };
  const [contructionInfo, setContructionInfo] = useState(initContructionInfo);
  const { startLoading, finishLoading } = useSetLoading();
  const setAlert = useSetRecoilState(alertState);
  const [constructionAgreementInfo, setConstructionAgreementInfo] =
    useRecoilState<ConstructionAgreementInfoStateType>(ConstructionAgreementInfoState);
  const { execute: getConstructionAgreement } = useGetConstructionAgreementInfo();
  // オプション情報
  const optionsInfo = useRecoilValue(OptionsInfoState);
  const isHandsupOption =
    optionsInfo.options.filter((option) => option.code === Constants.OptionCode.HANDS_UP).length >
    0;
  useEffect(() => {
    (async () => {
      // 工事残債は一覧表示に直接使うので、画面表示時に最新値を取得する。
      startLoading(ApiUrl.Read.CONSTRUCTION_INFO);
      const headers = await getSigV4HeadersForGet(ApiUrl.Read.CONSTRUCTION_INFO);
      await api
        .get(ApiUrl.Read.CONSTRUCTION_INFO, headers)
        .then((res) => {
          setContructionInfo(res.data);
        })
        .catch((error: any) => {
          if (isExpiredToken(error)) {
            sessionStorage.clear();
            window.location.href = url('/login');
            return;
          }
          setAlert({
            isOpen: true,
            apiType: Constants.ApiType.API_GET,
          });
        })
        .finally(() => finishLoading(ApiUrl.Read.CONSTRUCTION_INFO));
    })();
    callAPIGetConstructionAgreementInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 工事承諾情報取得API呼び出し
  const callAPIGetConstructionAgreementInfo = async () => {
    try {
      startLoading(ApiUrl.Read.CONSTRUCTION_AGREEMENT_INFO);
      const { data }: AxiosResponse<ConstructionAgreementInfoResponse, any> =
        await getConstructionAgreement();
      // 取得した値をセット
      setConstructionAgreementInfo(data);
    } catch (error: any) {
      if (isExpiredToken(error)) {
        sessionStorage.clear();
        window.location.href = url('/login');
        return;
      }
      setAlert({
        isOpen: true,
        apiType: Constants.ApiType.API_GET,
      });
    } finally {
      finishLoading(ApiUrl.Read.CONSTRUCTION_AGREEMENT_INFO);
    }
  };

  return (
    <div className='m-box-02' id={anchor}>
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='construction'>
          工事情報
        </Heading>

        <Heading as='h3'>工事費残債情報</Heading>
        <div className='m-table'>
          <table>
            <colgroup>
              <col />
              <col />
            </colgroup>
            <tbody>
              <tr>
                <th className='m-table__th'>工事費残債お支払い回数</th>
                <td
                  className='m-table__td'
                  data-testid='construction_remaining_count'
                >{`あと${contructionInfo.construction_remaining_count}回`}</td>
              </tr>
              <tr>
                <th className='m-table__th'>工事費残債合計額</th>
                <td
                  className='m-table__td'
                  data-testid='construction_remaining_price'
                >{`あと${contructionInfo.construction_remaining_price.toLocaleString()}円`}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul className='m-list-notice'>
          <li className='m-list-notice__item'>
            表示されている金額は、お客さまの契約内容や解約時期、キャンペーンの適用状況によって実際の請求額と異なる場合がございます。正しい請求金額は解約完了後に確定いたします。詳細は契約内容書面をご確認ください。
          </li>
          <li className='m-list-notice__item'>
            開通月から経過した月数に基づいた回数と金額を表示しています。（表示金額は開通月時点の消費税率が適用されています。）
          </li>
          <li className='m-list-notice__item'>
            工事費を分割でお支払い期間中に「NURO
            光」を解約した場合は、上記残債金額を一括でお支払いいただきます。
          </li>
          <li className='m-list-notice__item'>
            お支払い情報については、「お支払い状況一覧」よりご確認ください。
          </li>
        </ul>

        {/* Hands-up 契約者だけ追加の設備導入残債を表示する。 */}
        {isHandsupOption ? <HandsupInfo /> : ''}
        {constructionAgreementInfo.construction_approval_list.some(
          (item) => item.service_id.length > 0
        ) && (
          <>
            <Heading as='h3'>工事承諾</Heading>
            <div className='m-button align-center'>
              <LinkButton variant='secondary' href='/contract/construction'>
                回線引込み工事承諾はこちら
              </LinkButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConstructionInfo;
